import { ArrowLeft } from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../breadcrumb";

import { useDocumentTitleFromBreadcrumbs } from "./hooks";
import { BreadcrumbItem as BreadcrumbItemType, BreadcrumbsProps } from "./types";

import { LinkWithSearch } from "@/components/LinkWithSearch";
import { Title } from "@/components/ui/ResourceHeader/Title";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { EntityIcon } from "@/components/ui/icons";
import { getItemColor } from "@/components/UnifiedRegistry/utils";
import { useNavigateWithSearch } from "@/hooks";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const MAX_VISIBLE_ITEMS = 4; // Show first, ellipsis, and last 2 items when overflow

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  showBackButton = false,
  backButtonLabel = "Back",
  onBackClick,
  className = "",
  ariaLabel = "Breadcrumb",
  onItemClick,
  maxVisibleItems = MAX_VISIBLE_ITEMS,
  updateDocumentTitle,
  entityTitle,
}) => {
  const navigate = useNavigate();
  const navigateWithSearch = useNavigateWithSearch();

  useDocumentTitleFromBreadcrumbs(items, updateDocumentTitle ?? !onItemClick);

  const handleBackClick = () => {
    const parentHref = items[items.length - 2]?.href;

    if (onBackClick) {
      onBackClick();
    } else if (parentHref) {
      if (onItemClick) {
        onItemClick(items[items.length - 2]);
      } else if (parentHref.includes("?")) {
        navigate(parentHref);
      } else {
        navigateWithSearch(parentHref);
      }
    } else if (!onItemClick) {
      navigate(-1);
    }
  };

  const handleItemClick = (item: BreadcrumbItemType) => (e: React.MouseEvent) => {
    if (onItemClick) {
      e.preventDefault();
      onItemClick(item);
    }
  };

  const { visibleItems, hiddenItems, showEllipsis } = useMemo(() => {
    if (items.length <= maxVisibleItems) {
      return {
        visibleItems: items,
        hiddenItems: [],
        showEllipsis: false,
      };
    }

    const firstItem = items[0];
    const lastTwoItems = items.slice(-2);
    const hidden = items.slice(1, -2);

    const visible = [
      firstItem,
      null, // Placeholder for ellipsis
      ...lastTwoItems,
    ].filter(Boolean) as BreadcrumbItemType[];

    return {
      visibleItems: visible,
      hiddenItems: hidden,
      showEllipsis: true,
    };
  }, [items, maxVisibleItems]);

  if (items.length === 0) {
    return null;
  }

  const renderBreadcrumbContent = (
    item: BreadcrumbItemType,
    isClickable: boolean,
    isCurrentItem = false,
  ) => {
    const content =
      isCurrentItem && entityTitle ? (
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
              getItemColor(entityTitle.type),
            )}
          >
            <EntityIcon type={entityTitle.type} size="xs" />
          </span>
          {entityTitle.isLoading && !entityTitle.title ? (
            <span className="h-4 w-28 animate-pulse rounded bg-muted" />
          ) : (
            <Title
              title={entityTitle.title}
              onTitleEdit={entityTitle.onTitleEdit}
              badgeText={entityTitle.badgeText}
              badgeVariant={entityTitle.badgeVariant}
              type={entityTitle.type}
              variant="breadcrumb"
            />
          )}
        </span>
      ) : (
        <span className="flex min-w-0 items-center">
          {item.icon && <span className="mr-1.5 shrink-0">{item.icon}</span>}
          <OverflowDisplayTooltip
            content={item.label}
            className="min-w-0 max-w-[150px] lg:max-w-[200px]"
          >
            <span className="truncate">{item.label}</span>
          </OverflowDisplayTooltip>
        </span>
      );

    if (isClickable && onItemClick) {
      return (
        <BreadcrumbLink asChild className="min-w-0">
          <button
            type="button"
            className="flex min-w-0 items-center"
            onClick={handleItemClick(item)}
          >
            {content}
          </button>
        </BreadcrumbLink>
      );
    }

    if (isClickable && item.href) {
      return (
        <BreadcrumbLink asChild className="min-w-0">
          <LinkWithSearch
            path={item.href}
            className="flex min-w-0 items-center"
            onClick={handleItemClick(item)}
          >
            {content}
          </LinkWithSearch>
        </BreadcrumbLink>
      );
    }

    return <BreadcrumbPage className="flex min-w-0 items-center">{content}</BreadcrumbPage>;
  };

  return (
    <div
      className={cn("flex min-w-0 items-center gap-3", entityTitle ? "min-h-8" : "mb-6", className)}
    >
      {showBackButton && (
        <>
          <button
            onClick={handleBackClick}
            className="flex shrink-0 items-center text-sm text-muted-foreground transition-all duration-200 hover:text-foreground"
            aria-label="Go back"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            <span>{backButtonLabel}</span>
          </button>
          <span className="shrink-0 text-muted-foreground/50">|</span>
        </>
      )}

      <Breadcrumb aria-label={ariaLabel} className="min-w-0 flex-1 overflow-hidden">
        <BreadcrumbList className="flex-nowrap">
          {showEllipsis ? (
            <>
              <BreadcrumbItem className="min-w-0">
                {renderBreadcrumbContent(visibleItems[0], true)}
              </BreadcrumbItem>
              <BreadcrumbSeparator className="shrink-0" />

              <BreadcrumbItem className="shrink-0">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="flex items-center justify-center rounded-md transition-colors hover:bg-accent"
                      aria-label="Show hidden breadcrumb items"
                    >
                      <BreadcrumbEllipsis className="h-9 w-9" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto max-w-[300px] p-1" align="start" sideOffset={8}>
                    <div className="flex flex-col">
                      {hiddenItems.map((item, index) =>
                        onItemClick ? (
                          <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={`hidden-${item.label}-${index}`}
                            type="button"
                            className={cn(
                              "flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm",
                              "hover:bg-accent hover:text-accent-foreground",
                              "transition-colors",
                            )}
                            onClick={handleItemClick(item)}
                          >
                            {item.icon && <span className="shrink-0">{item.icon}</span>}
                            <OverflowDisplayTooltip content={item.label} className="min-w-0 flex-1">
                              <span className="truncate">{item.label}</span>
                            </OverflowDisplayTooltip>
                          </button>
                        ) : (
                          <LinkWithSearch
                            // eslint-disable-next-line react/no-array-index-key
                            key={`hidden-${item.label}-${index}`}
                            path={item.href || "#"}
                            className={cn(
                              "flex items-center gap-2 rounded-sm px-3 py-2 text-sm",
                              "hover:bg-accent hover:text-accent-foreground",
                              "transition-colors",
                              !item.href && "pointer-events-none opacity-60",
                            )}
                            onClick={handleItemClick(item)}
                          >
                            {item.icon && <span className="shrink-0">{item.icon}</span>}
                            <OverflowDisplayTooltip content={item.label} className="min-w-0 flex-1">
                              <span className="truncate">{item.label}</span>
                            </OverflowDisplayTooltip>
                          </LinkWithSearch>
                        ),
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="shrink-0" />

              {visibleItems.slice(1).map((item, index) => {
                const actualIndex = items.length - 2 + index;
                const isLast = index === visibleItems.slice(1).length - 1;

                return (
                  <React.Fragment key={`${item.label}-${actualIndex}`}>
                    <BreadcrumbItem className="min-w-0">
                      {renderBreadcrumbContent(item, !isLast, isLast)}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator className="shrink-0" />}
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            items.map((item, index) => {
              const isLast = index === items.length - 1;

              return (
                <React.Fragment
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item.label}-${index}`}
                >
                  <BreadcrumbItem className="min-w-0">
                    {renderBreadcrumbContent(item, !isLast, isLast)}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator className="shrink-0" />}
                </React.Fragment>
              );
            })
          )}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default Breadcrumbs;
export type { BreadcrumbItemType as BreadcrumbItem, BreadcrumbsProps };
